import {
  Box,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Spinner,
} from '@chakra-ui/react'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { RequestorStatsRow } from '@/components/dashboard'
import { dashboardService } from '@/lib/services/dashboardService'
import { format, parseISO } from 'date-fns'

const REQUESTOR_STATUS_BADGE: Record<string, string> = {
  Draft: 'gray',
  New: 'blue',
  Pending: 'yellow',
  'In Progress': 'orange',
  Resolved: 'green',
  Closed: 'gray',
  Cancelled: 'red',
  Assigned: 'purple',
  'For Approval': 'orange',
  Approved: 'green',
  Rejected: 'red',
}

export default function RequestorDashboardPage() {
  const colors = useThemeColors()
  const [stats, setStats] = useState<{
    total_requests: number
    in_progress: number
    open_requests: number
    resolved_requests: number
  } | null>(null)
  const [latestRequests, setLatestRequests] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [requestsLoading, setRequestsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await dashboardService.getRequestorStats()
        setStats(data)
      } catch (err: any) {
        console.error('Requestor dashboard stats:', err)
        setError(err.message || 'Failed to load dashboard data')
      } finally {
        setIsLoading(false)
      }
    }
    fetchStats()
  }, [])

  useEffect(() => {
    const fetchLatest = async () => {
      try {
        setRequestsLoading(true)
        const { data } = await dashboardService.getRequestorLatestRequests()
        setLatestRequests(Array.isArray(data) ? data.slice(0, 10) : [])
      } catch {
        setLatestRequests([])
      } finally {
        setRequestsLoading(false)
      }
    }
    fetchLatest()
  }, [])

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column" w="100%" minW={0}>
        <VStack spacing={4} align="stretch" flex="1" overflow="hidden" minH={0} w="100%">
          {error && (
            <Alert status="error" borderRadius="md" flexShrink={0}>
              <AlertIcon />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Stats row always visible (like actionlabs-support-client Dashboard) */}
          <Box flexShrink={0} w="100%" minW={0}>
            <RequestorStatsRow
              totalRequests={
                stats
                  ? (typeof stats.total_requests === 'number' && stats.total_requests > 0
                      ? stats.total_requests
                      : (stats.in_progress ?? 0) + (stats.open_requests ?? 0) + (stats.resolved_requests ?? 0))
                  : 0
              }
              inProgress={stats?.in_progress ?? 0}
              openRequests={stats?.open_requests ?? 0}
              resolvedRequests={stats?.resolved_requests ?? 0}
            />
          </Box>

          {!isLoading && !stats && !error && (
            <Box
              flexShrink={0}
              p={4}
              textAlign="center"
              borderRadius="md"
              bg={colors.cardBg}
              border="1px"
              borderColor={colors.border}
            >
              <Text color={colors.textMuted} fontSize="sm">
                Submit a request to see your stats here.
              </Text>
            </Box>
          )}

          <Card
            flex="1"
            minH={0}
            display="flex"
            flexDirection="column"
            overflow="hidden"
            bg={colors.cardBg}
            borderWidth="1px"
            borderColor={colors.border}
          >
            <CardBody flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden" p={4}>
              <VStack align="stretch" spacing={4} flex="1" minH={0} overflow="hidden">
                <Box display="flex" justifyContent="space-between" alignItems="center" flexShrink={0}>
                  <Text fontWeight="semibold" fontSize="lg" color={colors.text}>
                    First 10 ticket requests
                  </Text>
                  <Button
                    as={RouterLink}
                    to="/my-request"
                    size="sm"
                    variant="outline"
                    colorScheme="blue"
                  >
                    View all
                  </Button>
                </Box>
                {requestsLoading ? (
                  <Box py={6} textAlign="center" flexShrink={0}>
                    <Spinner size="md" color="blue.500" />
                  </Box>
                ) : latestRequests.length === 0 ? (
                  <Text color={colors.textMuted} fontSize="sm" py={4} flexShrink={0}>
                    No ticket requests yet. Create one from My Requests.
                  </Text>
                ) : (
                  <Box flex="1" minH={0} overflowY="auto" overflowX="hidden" w="100%">
                    <Table size="sm" variant="simple" w="100%">
                      <Thead position="sticky" top={0} bg={colors.cardBg} zIndex={1}>
                        <Tr>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">
                            Request #
                          </Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">
                            Service
                          </Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">
                            Status
                          </Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">
                            Date created
                          </Th>
                          <Th color={colors.textSecondary} whiteSpace="nowrap">
                            Last updated
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {latestRequests.map((row: any) => (
                          <Tr key={row.id}>
                            <Td whiteSpace="nowrap" fontSize="sm">
                              {row.request_number ?? '—'}
                            </Td>
                            <Td whiteSpace="nowrap">
                              {row.service_type_name ?? row.service_type?.name ?? '—'}
                            </Td>
                            <Td whiteSpace="nowrap">
                              <Badge
                                colorScheme={
                                  REQUESTOR_STATUS_BADGE[row.ticket_status_label as string] ?? 'gray'
                                }
                                variant="solid"
                                fontSize="xs"
                              >
                                {row.ticket_status_label ?? '—'}
                              </Badge>
                            </Td>
                            <Td whiteSpace="nowrap">
                              {row.created_at
                                ? (() => {
                                    try {
                                      return format(
                                        typeof row.created_at === 'string'
                                          ? parseISO(row.created_at)
                                          : new Date(row.created_at),
                                        'MMM d, yyyy · h:mm a'
                                      )
                                    } catch {
                                      return row.created_at
                                    }
                                  })()
                                : '—'}
                            </Td>
                            <Td whiteSpace="nowrap" color={colors.textSecondary} fontSize="sm">
                              {row.last_updated_at_human ?? '—'}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Box>
    </AdminLayout>
  )
}
